import devotionalData from '../data/devotionals.json';
import verseCatalog from '../data/verseCatalog.json';
import { supabase, isSupabaseConfigured } from './supabaseClient';

// Treat a date-only string as a local calendar date, never as midnight UTC.
const localDate = (date = new Date()) => {
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number);
        return new Date(year, month - 1, day, 12);
    }
    return new Date(date);
};

export const getLocalDateKey = (date = new Date()) => {
    const value = localDate(date);
    return [
        value.getFullYear(),
        String(value.getMonth() + 1).padStart(2, '0'),
        String(value.getDate()).padStart(2, '0')
    ].join('-');
};

// UTC is used only to count calendar days after extracting local date parts.
// Daylight-saving changes cannot advance or repeat a day's selection.
const calendarDay = (date) => {
    const value = localDate(date);
    return Math.floor(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / 86400000);
};

const hashId = (id) => {
    let hash = 2166136261;
    for (const character of String(id)) {
        hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
    }
    return hash >>> 0;
};

const shuffled = (items) => [...items].sort((a, b) => hashId(a.id) - hashId(b.id));
// Use the catalog builder's intentional daily-reading seed list. The remaining
// keyword-ranked candidates can require context and are not daily recommendations.
const verses = shuffled(verseCatalog.items.filter((item) => item.selectionBasis === 'editorial-priority-list'));
const devotionals = shuffled(devotionalData);
const selectForDate = (items, date) => items[((calendarDay(date) % items.length) + items.length) % items.length];

// Ready-to-read content is always available without an API call or setup flow.
// The daily seed verses retain their exact local Bible text and references;
// the full 500-entry catalog stays intact. This pool and the six complete
// devotional/prayer readings rotate rather than promising 365 unique texts.
export const getDailyVerse = (date = new Date()) => ({
    ...selectForDate(verses, date),
    date: getLocalDateKey(date)
});

export const getLocalDailyDevotional = (date = new Date()) => ({
    ...selectForDate(devotionals, date),
    readingDate: getLocalDateKey(date)
});

export const getDailyPrayer = (date = new Date()) => {
    const devotional = getLocalDailyDevotional(date);
    return {
        id: `prayer-${devotional.id}`,
        date: devotional.readingDate,
        title: devotional.title,
        scriptureReference: devotional.scripture,
        scripture: devotional.scriptureText,
        prayer: devotional.prayer,
        reflection: devotional.application[0]
    };
};

// Each item really is the selection for its displayed date, including at year
// and daylight-saving boundaries. The default shows each library item once.
export const getDevotionalHistory = (date = new Date(), count = devotionals.length) => {
    const day = localDate(date);
    return Array.from({ length: count }, (_, offset) => {
        const previous = new Date(day);
        previous.setDate(previous.getDate() - offset);
        return getLocalDailyDevotional(previous);
    });
};

// Preserve access to the existing pre-generated content bank for optional
// callers. Daily navigation uses the bundled selectors above and never waits
// for this service or requests AI generation.
const getDailyContent = async (kind, date = getLocalDateKey()) => {
    if (!isSupabaseConfigured) return null;
    const key = getLocalDateKey(date);
    try {
        const { data, error } = await supabase
            .from('daily_content')
            .select('content')
            .eq('kind', kind)
            .eq('date', key)
            .eq('status', 'published')
            .maybeSingle();
        if (error) throw error;
        return data?.content || null;
    } catch (err) {
        console.warn(`[dailyContent] ${kind} ${key} unavailable:`, err.message);
        return null;
    }
};

export const getVerseOfTheDay = (date) => getDailyContent('daily_verse', date);
export const getDailyDevotion = (date) => getDailyContent('daily_devotion', date);

export default {
    getLocalDateKey,
    getDailyVerse,
    getDailyPrayer,
    getLocalDailyDevotional,
    getDevotionalHistory,
    getVerseOfTheDay,
    getDailyDevotion
};
